import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  LibraryBooks as LibraryIcon,
} from "@mui/icons-material";
import axios from "axios";
import api from "../api";
import { getWidgetConfig, cloudinaryConfig } from "../config/cloudinary";
import CloudinaryMediaLibrary from "./CloudinaryMediaLibrary";

interface UploadedImage {
  url: string;
  public_id: string;
  thumbnail_url: string;
  large_url: string;
  width?: number;
  height?: number;
}

interface ImageUploadProps {
  onImageUploaded: (image: UploadedImage) => void;
  onRemoveImage?: () => void;
  currentImageUrl?: string;
  label?: string;
  disabled?: boolean;
  showCloudinaryWidget?: boolean;
  maxImages?: number;
  currentImageCount?: number;
  currentImageUrls?: string[];
  onDuplicateImages?: (imageNames: string[]) => void;
}

const loadCloudinaryWidget = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.cloudinary) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://widget.cloudinary.com/v2.0/global/all.js"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://widget.cloudinary.com/v2.0/global/all.js";
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Cloudinary upload widget"));

    document.body.appendChild(script);
  });
};

const ImageUpload = ({
  onImageUploaded,
  onRemoveImage,
  currentImageUrl = "",
  label = "Upload Image",
  disabled = false,
  showCloudinaryWidget = true,
  maxImages = 5,
  currentImageCount = 0,
  currentImageUrls = [],
  onDuplicateImages,
}: ImageUploadProps) => {
  const remainingImageSlots = Math.max(0, maxImages - currentImageCount);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update preview when currentImageUrl changes
  useEffect(() => {
    setPreviewUrl(currentImageUrl);
  }, [currentImageUrl]);

  const openCloudinaryWidget = async () => {
    try {
      await loadCloudinaryWidget();
    } catch (error) {
      console.error("Cloudinary widget failed to load:", error);
      setError("Cloudinary widget not available. Please try again.");
      return;
    }

    if (!window.cloudinary) {
      setError("Cloudinary widget not available. Please try again.");
      return;
    }

    console.log("Opening Cloudinary widget");
    console.log("Widget config:", getWidgetConfig());

    const widget = window.cloudinary.createUploadWidget(
      getWidgetConfig(),
      (error: unknown, result: CloudinaryUploadResult) => {
        if (error) {
          console.error("Cloudinary widget error:", error);
          setError("Upload failed. Please try again.");
          return;
        }

        if (!result) {
          return;
        }

        console.log(
          "Cloudinary widget event received:",
          result.event,
          result.info,
        );

        if (result.event === "success") {
          const imageData: UploadedImage = {
            url: result.info.secure_url,
            public_id: result.info.public_id,
            thumbnail_url: result.info.thumbnail_url || result.info.secure_url,
            large_url: result.info.secure_url,
            width: result.info.width,
            height: result.info.height,
          };

          setPreviewUrl(imageData.url);
          setError("");
          onImageUploaded(imageData);
        }
      },
    );

    widget.open();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("File size must be less than 5MB.");
      return;
    }

    setError("");

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        setPreviewUrl(reader.result);
      }
    };

    reader.readAsDataURL(file);
    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No authentication token found");
      }

      const formData = new FormData();
      formData.append("image", file);
      formData.append("folder", "Icons");

      const response = await api.post("/upload/single", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.data) {
        const imageData = response.data.data;

        setPreviewUrl(imageData.url);

        if (onImageUploaded) {
          onImageUploaded(imageData);
        }
      }
    } catch (error: unknown) {
      console.error("Upload error:", error);

      if (axios.isAxiosError(error)) {
        setError(error.response?.data?.message || "Failed to upload image.");
      } else {
        setError("Failed to upload image.");
      }

      setPreviewUrl(currentImageUrl);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    if (onRemoveImage) {
      onRemoveImage();
    }
  };

  const handleClick = () => {
    if (disabled || uploading) {
      return;
    }

    const isCloudinaryConfigured =
      cloudinaryConfig.cloudName !== "your-cloud-name";

    if (showCloudinaryWidget && isCloudinaryConfigured) {
      console.log("Using Cloudinary upload widget");
      openCloudinaryWidget();
    } else {
      console.log("Using manual image upload");
      fileInputRef.current?.click();
    }
  };

  const handleManualUpload = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const handleImagesSelectedFromLibrary = (
    selectedImages: CloudinaryUploadInfo[],
  ) => {
    console.log("Images selected from library:", selectedImages);

    const duplicateImages = selectedImages.filter((selectedImage) =>
      currentImageUrls.includes(selectedImage.secure_url),
    );

    const newImages = selectedImages.filter(
      (selectedImage) => !currentImageUrls.includes(selectedImage.secure_url),
    );

    if (duplicateImages.length > 0 && onDuplicateImages) {
      const duplicateNames = duplicateImages.map(
        (image) => image.public_id?.split("/").pop() || "selected image",
      );

      onDuplicateImages(duplicateNames);
    }

    newImages.forEach((selectedImage) => {
      const imageData: UploadedImage = {
        url: selectedImage.secure_url,
        public_id: selectedImage.public_id,
        thumbnail_url: selectedImage.thumbnail_url || selectedImage.secure_url,
        large_url: selectedImage.secure_url,
        width: selectedImage.width,
        height: selectedImage.height,
      };

      onImageUploaded(imageData);
    });

    const lastNewImage = newImages[newImages.length - 1];

    if (lastNewImage) {
      setPreviewUrl(lastNewImage.secure_url);
    }

    setError("");
  };

  // Single image mode
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          textAlign: "center",
          cursor: disabled || uploading ? "not-allowed" : "pointer",
          opacity: disabled || uploading ? 0.6 : 1,
          position: "relative",
        }}
        onClick={handleClick}
      >
        {uploading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 1,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {previewUrl ? (
          <Box sx={{ position: "relative" }}>
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "200px",
                objectFit: "contain",
                borderRadius: "4px",
              }}
            />
            {!disabled && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveImage();
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  },
                }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {showCloudinaryWidget
                ? "Click to upload image"
                : "Click to select image"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PNG, JPG up to 5MB
            </Typography>
            {showCloudinaryWidget && (
              <Typography
                variant="caption"
                color="primary"
                display="block"
                sx={{ mt: 1 }}
              >
                {cloudinaryConfig.cloudName === "your-cloud-name"
                  ? "Configure Cloudinary for enhanced uploads"
                  : "Uses Cloudinary for better upload experience"}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {showCloudinaryWidget && (
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={handleManualUpload}
          disabled={disabled || uploading}
          sx={{ mt: 1, mr: 1 }}
          startIcon={<ImageIcon />}
        >
          Manual Upload
        </Button>
      )}

      <Button
        type="button"
        variant="outlined"
        size="small"
        onClick={() => {
          console.log("🔧 Single mode Select from Library button clicked");
          setShowMediaLibrary(true);
        }}
        disabled={disabled || remainingImageSlots === 0}
        sx={{ mt: 1 }}
        startIcon={<LibraryIcon />}
      >
        {remainingImageSlots === 0 ? "Maximum 5 Images" : "Select from Library"}
      </Button>

      {remainingImageSlots === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Maximum 5 images. Delete an image before selecting another.
        </Alert>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        disabled={disabled || uploading}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Media Library Dialog */}
      <CloudinaryMediaLibrary
        open={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onImagesSelected={handleImagesSelectedFromLibrary}
        folder="Icons"
        title="Select Images from Library"
        maxSelection={remainingImageSlots}
      />
    </Box>
  );
};

export default ImageUpload;
